import { createFileRoute, redirect } from '@tanstack/react-router';
import { useCallback } from 'react';
import { z } from 'zod';
import { LoginForm } from '@/components/auth/login-form';
import { LoginHero } from '@/components/auth/login-hero';
import { SignupForm } from '@/components/auth/signup-form';
import { authClient } from '@/lib/auth-client';
import { EXTENSION_LOGIN_POPUP_QUERY_VALUE } from '@/lib/extension-login-popup';
import { buildMeta } from '@/lib/seo';

const loginSearchSchema = z.object({
  redirect: z.string().optional(),
  mode: z.enum(['signin', 'signup']).optional(),
  // When this page is opened as a popup by the browser extension's tracker
  // modal, `popup=extension` tells us to close ourselves on successful
  // sign-in (instead of navigating via TanStack Router) so the user returns
  // to KSP with the session set. The modal itself was closed when the popup
  // opened; the user re-clicks "Track this item" to authenticate.
  popup: z.literal(EXTENSION_LOGIN_POPUP_QUERY_VALUE).optional(),
});

export const Route = createFileRoute('/_public/login')({
  ssr: false,
  head: () => ({
    meta: buildMeta({
      title: 'התחברות - Pricedrop',
      description: '',
      canonicalUrl: '/login',
      noindex: true,
    }),
  }),
  validateSearch: loginSearchSchema,
  beforeLoad: async ({ search }) => {
    const { data: session } = await authClient.getSession();
    if (session) {
      // Already signed in. If this was opened as the extension popup, close
      // ourselves so the user returns to KSP with the session ready. Fall
      // through to the normal redirect in case window.close() is ignored
      // (only happens if the page wasn't opened via window.open).
      if (
        search.popup === EXTENSION_LOGIN_POPUP_QUERY_VALUE &&
        typeof window !== 'undefined' &&
        window.opener
      ) {
        window.close();
      }
      throw redirect({
        to: search.redirect || '/',
      });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const { redirect: redirectTo, mode, popup } = Route.useSearch();
  const isExtensionPopup = popup === EXTENSION_LOGIN_POPUP_QUERY_VALUE;

  // Extension-popup onSuccess: just close the window. The modal was already
  // closed when the popup opened; when the user returns to KSP and re-opens
  // the tracker, a fresh modal mount authenticates cleanly.
  const handleExtensionSuccess = useCallback(() => {
    if (typeof window === 'undefined') return;
    window.close();
  }, []);

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 md:py-12">
      <div className="grid items-start gap-8 lg:grid-cols-2 lg:gap-12">
        {isExtensionPopup ? null : <LoginHero />}
        <div className="w-full max-w-md justify-self-center lg:justify-self-start">
          {mode === 'signup' ? (
            <SignupForm
              redirectTo={redirectTo}
              onSuccess={isExtensionPopup ? handleExtensionSuccess : undefined}
            />
          ) : (
            <LoginForm
              redirectTo={redirectTo}
              onSuccess={isExtensionPopup ? handleExtensionSuccess : undefined}
              compact={isExtensionPopup}
            />
          )}
        </div>
      </div>
    </div>
  );
}
